// NewsPage — In-app news & announcements
// Layout:
//   - Top nav: < Back | News
//   - Featured (large card) + list of recent

import { useNavigate } from 'react-router-dom';



interface NewsItem {
  id: string;
  category: 'Announcement' | 'Feature' | 'Market' | 'Tip';
  title: string;
  body: string;
  date: string;
  read?: boolean;
  pinned?: boolean;
}




const NEWS: NewsItem[] = [
  { id: '1', category: 'Feature',      pinned: true, read: false,
    title: 'AI Bot Trader is now available for all tiers',
    body: 'Run automated futures strategies 24/7. Choose from Breakout, Mean Reversion, Trend Follow, and Sniper — each tier-gated to the right plan.',
    date: '2026-07-24',
  },
  { id: '2', category: 'Announcement', pinned: true, read: false,
    title: 'New Professional tier with 90% profit split',
    body: 'Get up to 90% of your profits, 5 funded challenges, all 4 AI bot strategies, API access, and priority support.',
    date: '2026-07-22',
  },
  { id: '3', category: 'Market',
    title: 'FOMC minutes release tomorrow — expect volatility',
    body: 'High-impact news at 2:00 PM ET. New positions will be restricted 2 minutes before/after per our risk policy.',
    date: '2026-07-23',
    read: false,
  },
  { id: '4', category: 'Feature',
    title: 'Live Economic Calendar now in dashboard',
    body: 'Track high-impact news events with country flags. Toggle "Show only restricted events" to focus on what matters for your bot.',
    date: '2026-07-20',
    read: false,
  },
  { id: '5', category: 'Tip',
    title: 'Best practices for scaling a funded account',
    body: 'Start with 1-2 contracts. Add 1 contract per $5K profit. Never risk more than 2% per trade. Hit your target consistently before scaling.',
    date: '2026-07-18',
    read: false,
  },
  { id: '6', category: 'Announcement',
    title: 'Volume profile + TPO now live on Deepchart®',
    body: 'All Beginner tier users can now access the volume profile indicator and TPO chart on the Deepchart® platform. No extra cost.',
    date: '2026-07-15',
    read: false,
  },
];

const CAT_COLOR: Record<NewsItem['category'], string> = {
  Announcement: 'bg-tf-blue',
  Feature: 'bg-[#34C759]',
  Market: 'bg-[#FF9500]',
  Tip: 'bg-[#5856D6]',
};

export function NewsPage() {
  const nav = useNavigate();
  const featured = NEWS.find(n => n.pinned && !n.read) || NEWS[0];
  const others = NEWS.filter(n => n.id !== featured.id);

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">News & Updates</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 2l3 6 6-3-3 6 6 3-6 3 3 6-6-3-3 6-3-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Featured card */}
      <div className="px-4 pt-4">
        <button
          onClick={() => nav('/portal/news/' + featured.id)}
          data-testid="news-featured"
          className="relative w-full overflow-hidden rounded-[20px] bg-gradient-to-br from-[#1C1C1E] to-[#2A2A2A] p-5 text-left text-white active:scale-[0.99]"
        >
          <div className="flex items-center gap-2">
            <span className={['rounded-full px-2 py-0.5 text-[10px] font-bold uppercase', CAT_COLOR[featured.category]].join(' ')}>{featured.category}</span>
            <span className="rounded-full bg-[#FF9500] px-2 py-0.5 text-[10px] font-bold uppercase text-white">Pinned</span>
            {featured.read && <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold text-white/80">Read</span>}
          </div>
          <h2 className="mt-3 text-[20px] font-bold leading-tight">{featured.title}</h2>
          <p className="mt-2 text-[13px] leading-5 text-white/80 line-clamp-3">{featured.body}</p>
          <div className="mt-3 text-[11px] text-white/60">{new Date(featured.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
        </button>
      </div>

      {/* List */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Recent</h2>
      <div className="mx-4 space-y-2">
        {others.map((n) => (
          <button
            key={n.id}
            onClick={() => nav('/portal/news/' + n.id)}
            data-testid={`news-${n.id}`}
            className="flex w-full items-start gap-3 rounded-[14px] border border-tf-border bg-white p-3 text-left active:scale-[0.99]"
          >
            <div className={['flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[16px]', CAT_COLOR[n.category]].join(' ')}>
              {n.category === 'Feature' ? '✨' : n.category === 'Announcement' ? '📣' : n.category === 'Market' ? '📊' : '💡'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase text-white', CAT_COLOR[n.category]].join(' ')}>{n.category}</span>
                {n.read && <span className="rounded-full bg-tf-surface-1 px-1.5 py-0.5 text-[9px] font-semibold text-tf-text-3">Read</span>}
              </div>
              <div className="mt-1 text-[14px] font-semibold text-tf-text-1">{n.title}</div>
              <div className="text-[12px] text-tf-text-3 line-clamp-2">{n.body}</div>
              <div className="mt-1 text-[10px] text-tf-text-3">{new Date(n.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
